import React, { useMemo } from 'react';
import { Button, Tooltip } from '@planne-software/uni';
import { IconCircleCheck, IconDownload } from '@planne-software/uni/icons';
import { useMediaQuery } from '@planne-software/uni/mui/material';

import { useTranslate } from '@src/core/hooks';

import {
  ConfirmedOrderDescription,
  ConfirmedOrderDownloadRow,
  ConfirmedOrderHeaderRoot,
  ConfirmedOrderHeaderTitleRow,
  ConfirmedOrderTitle,
  ConfirmedOrderTitleRow,
} from '../styles';
import { tabletBreakpoint } from '@src/common/utils/mediaQuery';

export type ConfirmedOrderHeaderProps = {
  showsDownload: boolean;
  isDownloadBlocked: boolean;
  isDownloading: boolean;
  onDownload: () => void;
};

export const ConfirmedOrderHeader = (props: ConfirmedOrderHeaderProps) => {
  const { showsDownload, isDownloadBlocked, isDownloading, onDownload } = props;
  const { i18n } = useTranslate();
  const isSmallTablet = useMediaQuery(tabletBreakpoint);

  const downloadButton = useMemo(() => {
    if (showsDownload) {
      const button = (
        <Button
          data-testid='order-download-all'
          type='button'
          size='sm'
          loading={isDownloading}
          disabled={isDownloadBlocked || isDownloading}
          startSlot={<IconDownload size={20} aria-hidden />}
          onClick={onDownload}
        >
          {i18n('checkout_confirmed_download_all_vouchers')}
        </Button>
      );

      return (
        <ConfirmedOrderDownloadRow>
          {isDownloadBlocked ? (
            <Tooltip arrow placement='top' label={i18n('checkout_confirmed_vouchers_pending_tooltip')}>
              <span>{button}</span>
            </Tooltip>
          ) : (
            button
          )}
        </ConfirmedOrderDownloadRow>
      );
    }
    return null;
  }, [showsDownload, isDownloadBlocked, isDownloading, onDownload, i18n]);

  return (
    <ConfirmedOrderHeaderRoot data-testid='confirmed-order-header'>
      <ConfirmedOrderHeaderTitleRow>
        <ConfirmedOrderTitleRow>
          <IconCircleCheck size={24} aria-hidden />
          <ConfirmedOrderTitle>{i18n('checkout_confirmed_title')}</ConfirmedOrderTitle>
        </ConfirmedOrderTitleRow>

        {!isSmallTablet ? downloadButton : null}
      </ConfirmedOrderHeaderTitleRow>

      <ConfirmedOrderDescription>{i18n('checkout_confirmed_description')}</ConfirmedOrderDescription>

      {isSmallTablet ? downloadButton : null}
    </ConfirmedOrderHeaderRoot>
  );
};

export default ConfirmedOrderHeader;
